import { isAfter, isBefore } from "date-fns";

import { inferLiveExamEndDate } from "@/features/settings";
import {
  AssessmentSettings,
  AssessmentType,
  ExamTiming,
} from "@/generated/graphql";

type NarrowedSettings = Pick<
  AssessmentSettings,
  | "examTiming"
  | "examReadingTime"
  | "examWritingTime"
  | "examStartDate"
  | "examEndDate"
>;

/**
 * @returns whether the exam has closed now
 */
export const isExamClosed = (settings: NarrowedSettings): boolean => {
  const { examTiming, examStartDate, examEndDate, examReadingTime } = settings;
  if (examStartDate == null) return false;
  if (examTiming === ExamTiming.Live) {
    const inferredEndDate = new Date(
      inferLiveExamEndDate({
        ...settings,
        noExamReadingTime: examReadingTime == null || examReadingTime === 0,
      })
    );
    return isAfter(new Date(), inferredEndDate);
  } else if (examTiming === ExamTiming.Window) {
    if (examEndDate == null) return false;
    return isAfter(new Date(), new Date(examEndDate));
  }
  return false;
};

/**
 * @returns whether an exam is happening right now
 */
export const isExamInProgress = (settings: NarrowedSettings): boolean => {
  const { examTiming, examStartDate, examEndDate, examReadingTime } = settings;
  if (examStartDate == null) return false;
  if (examTiming === ExamTiming.Live) {
    const inferredEndDate = new Date(
      inferLiveExamEndDate({
        ...settings,
        noExamReadingTime: examReadingTime == null || examReadingTime === 0,
      })
    );
    return (
      isAfter(new Date(), new Date(examStartDate)) &&
      isBefore(new Date(), new Date(inferredEndDate))
    );
  } else if (examTiming === ExamTiming.Window) {
    if (examEndDate == null) return false;
    return (
      isAfter(new Date(), new Date(examStartDate)) &&
      isBefore(new Date(), new Date(examEndDate))
    );
  }
  return false;
};

/**
 * @returns whether an assessment is an exam.
 */
export function isExam({
  assessmentType,
}: {
  assessmentType: AssessmentType;
}): boolean {
  return assessmentType === AssessmentType.Exam;
}
